/* ============================================
   LeadAdjuntos — documentos de la oportunidad

   El mismo componente sirve para los documentos del
   lead (el brief, la propuesta firmada) y para los de
   una tarea puntual: cambia `tareaId`.

   Los archivos viven en R2; acá solo se listan, se
   suben y se borran.
   ============================================ */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import type { LeadAdjunto } from '@/lib/types'
import {
  IconPaperclip, IconTrash, IconImage, IconFileText, IconFile,
  IconExternalLink, IconUpload,
} from '@/components/icons/Icons'

const MAX_BYTES = 25 * 1024 * 1024

/*
  Los formatos que se rechazan sin salir del navegador. **No es la lista
  completa**: la que manda vive en `api/src/lib/archivos-peligrosos.ts` y es la
  que decide de verdad. Esta es un adelanto de las más comunes, para no gastar
  una subida de 25 MB en algo que el servidor va a rechazar igual.
*/
const EXT_RECHAZADAS = [
  'exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'dll', 'jar', 'apk', 'dmg', 'app',
  'ps1', 'vbs', 'js', 'sh', 'hta', 'reg', 'lnk',
  'svg', 'html', 'htm', 'xhtml', 'swf',
  'docm', 'xlsm', 'pptm',
]

/** Mira TODAS las extensiones: `factura.pdf.exe` es el truco de siempre. */
function extensionRechazada(nombre: string): string | null {
  const partes = nombre.toLowerCase().split('.').slice(1)
  return partes.find(ext => EXT_RECHAZADAS.includes(ext)) ?? null
}

interface LeadAdjuntosProps {
  leadId: number
  /** Sin valor: todos los del lead. Con valor: solo los de esa tarea. */
  tareaId?: number
  /** Versión de una línea, para meterla dentro de una tarea. */
  compacto?: boolean
  onCountChange?: (total: number) => void
}

function pesoLegible(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function IconoTipo({ tipo }: { tipo: string }) {
  if (tipo === 'imagen') return <IconImage size={14} />
  if (tipo === 'pdf' || tipo === 'documento') return <IconFileText size={14} />
  return <IconFile size={14} />
}

export default function LeadAdjuntos({ leadId, tareaId, compacto = false, onCountChange }: LeadAdjuntosProps) {
  const api = useApi()
  const [adjuntos, setAdjuntos] = useState<LeadAdjunto[]>([])
  const [cargando, setCargando] = useState(true)
  const [subiendo, setSubiendo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [arrastrando, setArrastrando] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const apiGet = api.get

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const query = tareaId != null ? `?tarea_id=${tareaId}` : ''
      setAdjuntos(await apiGet<LeadAdjunto[]>(`/api/leads/${leadId}/adjuntos${query}`))
    } catch (err) {
      console.error('Error cargando adjuntos:', err)
      setAdjuntos([])
    } finally {
      setCargando(false)
    }
  }, [apiGet, leadId, tareaId])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => { onCountChange?.(adjuntos.length) }, [adjuntos, onCountChange])

  async function subir(archivos: FileList | null) {
    if (!archivos || archivos.length === 0) return
    setError(null)

    // Se sube de a uno: así un archivo grande no tumba a los demás y el
    // error dice cuál falló.
    for (const archivo of Array.from(archivos)) {
      if (archivo.size > MAX_BYTES) {
        setError(`"${archivo.name}" supera los 25 MB`)
        continue
      }
      const ext = extensionRechazada(archivo.name)
      if (ext) {
        setError(`Los archivos .${ext} no se pueden subir por seguridad.`)
        continue
      }
      setSubiendo(true)
      try {
        const fd = new FormData()
        fd.append('file', archivo)
        if (tareaId != null) fd.append('tarea_id', String(tareaId))
        const creado = await api.upload<LeadAdjunto>(`/api/leads/${leadId}/adjuntos/upload`, fd)
        setAdjuntos(prev => [creado, ...prev])
      } catch (err: any) {
        console.error('Error subiendo adjunto:', err)
        setError(err?.message || `No se pudo subir "${archivo.name}"`)
      } finally {
        setSubiendo(false)
      }
    }

    if (inputRef.current) inputRef.current.value = ''
  }

  async function eliminar(adjunto: LeadAdjunto) {
    if (!confirm(`¿Eliminar "${adjunto.nombre}"? Se borra del almacenamiento y no se puede deshacer.`)) return
    try {
      await api.del(`/api/leads/adjuntos/${adjunto.id}`)
      setAdjuntos(prev => prev.filter(a => a.id !== adjunto.id))
    } catch (err) {
      console.error('Error eliminando adjunto:', err)
    }
  }

  function soltar(e: React.DragEvent) {
    e.preventDefault()
    setArrastrando(false)
    subir(e.dataTransfer.files)
  }

  const lista = (
    <div className="ld-adjuntos__lista">
      {adjuntos.map(a => (
        <div key={a.id} className="ld-adjunto">
          <span className="ld-adjunto__icono"><IconoTipo tipo={a.tipo} /></span>
          <a className="ld-adjunto__nombre" href={a.url} target="_blank" rel="noopener noreferrer" title={a.nombre}>
            {a.nombre} <IconExternalLink size={10} />
          </a>
          <span className="ld-adjunto__peso">{pesoLegible(a.size_bytes)}</span>
          <button type="button" className="btn-icon-mini btn-icon-mini--danger" onClick={() => eliminar(a)} title="Eliminar">
            <IconTrash size={11} />
          </button>
        </div>
      ))}
    </div>
  )

  if (compacto) {
    return (
      <div className="ld-adjuntos ld-adjuntos--compacto">
        {cargando ? (
          <span className="ld-adjuntos__hint">Cargando adjuntos...</span>
        ) : (
          <>
            {adjuntos.length > 0 && lista}
            <button type="button" className="ld-adjuntos__boton" onClick={() => inputRef.current?.click()} disabled={subiendo}>
              <IconPaperclip size={12} /> {subiendo ? 'Subiendo...' : 'Adjuntar archivo'}
            </button>
            {error && <span className="ld-adjuntos__error">{error}</span>}
          </>
        )}
        <input ref={inputRef} type="file" multiple className="ld-adjuntos__input" onChange={e => subir(e.target.files)} />
      </div>
    )
  }

  return (
    <div
      className={`ld-adjuntos${arrastrando ? ' ld-adjuntos--arrastrando' : ''}`}
      onDragOver={e => { e.preventDefault(); setArrastrando(true) }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={soltar}
    >
      {cargando ? (
        <span className="ld-adjuntos__hint">Cargando documentos...</span>
      ) : (
        <>
          {adjuntos.length > 0 && lista}

          <div className="ld-adjuntos__acciones">
            <button type="button" className="ld-adjuntos__boton" onClick={() => inputRef.current?.click()} disabled={subiendo}>
              <IconUpload size={13} /> {subiendo ? 'Subiendo...' : 'Subir documento'}
            </button>
            <span className="ld-adjuntos__hint">o arrastralo acá — imágenes, video, documentos, hasta 25 MB</span>
          </div>

          {error && <p className="ld-adjuntos__error">{error}</p>}
        </>
      )}

      <input ref={inputRef} type="file" multiple className="ld-adjuntos__input" onChange={e => subir(e.target.files)} />
    </div>
  )
}
